import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {createProtoPersister} from '@github-ui/react-query-persister'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  getIssuesIndexContentQueryDeps,
  ISSUES_INDEX_CONTENT_PERSISTER_PREFIX,
  normalizeIssuesIndexContentStorageQueryKey,
} from '@github-ui/issue-index-query-defaults'
import {
  type RepoIssuesDashboardContentResponse,
  RepoIssuesDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'

import {repoRouteBuilder} from '../config/repo-route-builder'
import type {IssuesListSecondaryResult} from '../components/IssuesListSurface'
import {repoDeferredToListItem} from '../helpers/repo-issues-deferred'
import {issuesIndexQueryResolver} from '../helpers/issues-index-query-resolver'

export const ISSUES_INDEX_PATHS = {
  index: '/:owner/:repo/issues',
  mentioned: '/:owner/:repo/issues/mentioned/:mentioned',
  assigned: '/:owner/:repo/issues/assigned/:assignee',
  createdBy: '/:owner/:repo/issues/created_by/:author',
  createdByApp: '/:owner/:repo/issues/created_by/app/:author',
  label: '/:owner/:repo/labels/:label',
} as const

interface IssuesIndexSecondaryDataEnvelope {
  payload: {
    issuesIndexSecondaryData?: unknown
  }
}

function contentError(data: unknown): Error | undefined {
  if (typeof data !== 'object' || data === null || !('payload' in data)) return new Error('Invalid issues response')
  const payload = data.payload
  if (typeof payload !== 'object' || payload === null) return new Error('Invalid issues response')
  if ('error' in payload && payload.error) return new Error('Unable to refresh issues')
  return undefined
}

const ISSUES_INDEX_CONTENT_PERSISTER_CONFIG = {
  alwaysRefetch: true,
  flag: 'issues_index_query_persistence',
  // Follow-up: evaluate Relay's server-selected TTL; keep retention client-only for now.
  maxAgeMs: 24 * 60 * 60 * 1000,
  getDataError: contentError,
  persistInitialData: true,
  prefix: ISSUES_INDEX_CONTENT_PERSISTER_PREFIX,
  restoreOnPreload: true,
  storageQueryKey: normalizeIssuesIndexContentStorageQueryKey,
  versioning: 'record',
  dataValidator: {
    name: 'issues-index-success',
    isValid: (data: unknown) => contentError(data) === undefined,
  },
} as const

function contentPersister(routeId: 'issuesIndexContentRoute') {
  return createProtoPersister({
    schema: RepoIssuesDashboardContentResponseSchema,
    ...ISSUES_INDEX_CONTENT_PERSISTER_CONFIG,
    sendAnalyticsEvent: (eventType, target?, payload = {}) => {
      sendEvent(eventType, {queryName: 'mainQuery', routeId, ...payload, target})
    },
  })
}

function queryDeps({pathname, searchParams}: {pathname: string; searchParams: URLSearchParams}) {
  return getIssuesIndexContentQueryDeps({
    pathname,
    query: searchParams.get('q'),
    page: searchParams.get('page'),
  })
}

function secondaryQuery() {
  return {
    queryName: 'secondary',
    queryDeps: ({
      pathname,
      searchParams,
      params,
    }: {
      pathname: string
      searchParams: URLSearchParams
      params: {owner: string; repo: string}
    }) => ({
      pathname: `/${params.owner}/${params.repo}/issues/deferred`,
      searchParams: {
        q: issuesIndexQueryResolver({pathname, searchParams}),
        page: searchParams.get('page') || '1',
      },
    }),
    queryFn: async (queryKey: Parameters<typeof queryFnFetchWithProtoSignal>[0]) => {
      const {json} = await queryFnFetchWithProtoSignal<IssuesIndexSecondaryDataEnvelope>(queryKey)
      const rawDeferred = json.payload.issuesIndexSecondaryData
      if (!rawDeferred) return {payload: {issuesIndexSecondaryData: {results: [] as IssuesListSecondaryResult[]}}}

      const response = fromJson({
        schema: RepoIssuesDashboardContentResponseSchema,
        raw: rawDeferred,
        routeId: 'issuesIndexSecondaryData',
        queryName: 'secondary',
      })
      return {
        payload: {
          issuesIndexSecondaryData: {
            results: response.deferred.map(repoDeferredToListItem).filter(item => item !== undefined),
          },
        },
      }
    },
  }
}

/**
 * Repo Issues#index content route. Clean paths are preserved so Rails can apply
 * their route parameters and return the matching embedded payload.
 */
export function buildIssuesIndexContentRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issuesIndexContentRoute', {
    path: ISSUES_INDEX_PATHS.index,
    queries: [
      mainQuery<RepoIssuesDashboardContentResponse>({
        schema: RepoIssuesDashboardContentResponseSchema,
        persister: contentPersister('issuesIndexContentRoute'),
        queryDeps,
      }),
      secondaryQuery(),
    ],
  })
}

export function buildIssuesLabelContentRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issuesLabelContentRoute', {
    path: ISSUES_INDEX_PATHS.label,
    queries: [
      mainQuery<RepoIssuesDashboardContentResponse>({
        schema: RepoIssuesDashboardContentResponseSchema,
        queryDeps,
      }),
      secondaryQuery(),
    ],
  })
}

export const issuesIndexContentRoute = buildIssuesIndexContentRoute(repoRouteBuilder)
export const issuesLabelContentRoute = buildIssuesLabelContentRoute(repoRouteBuilder)
